import { BrowserRouter, Route, Routes } from 'react-router-dom'
import useMetaTags from 'react-metatags-hook'
import { PropsWithChildren, useEffect } from 'react'
import BaseContainer from '../containers/BaseContainer'
import ErrorBoundaries from '../containers/ErrorBoundaries'
import NotFound from '../containers/404'
import { useAppSelector } from '../hooks'
import { ITextLibrary, LoadersType } from '../interfaces'
import Config from '../containers/Config'
import ErrorMessage, { ActionType } from '../components/ErrorMessage'
import getUrlParams from '../utils/getUrlParams'
import getGoToChannelUrl from '../utils/common/getGoToChannelUrl'
import { WorkflowChannel } from '../graphQL/queries/getSession/types'
import flatTexts from '../utils/flatTexts'

/**
 * The loaders raised from below the route table, which therefore must not
 * gate it. A denylist of exemptions: anything not named here is a boot
 * loader. See the comment above `isBooting` for why each one is here.
 */
const NON_BOOT_LOADERS: string[] = [
  LoadersType.CREATE_ORDER,
  LoadersType.WORKFLOW_STEP,
  LoadersType.RELOAD_PAGE,
]

const Index = ({ children }: PropsWithChildren) => {
  const config = useAppSelector((state) => state.defaultSlice.config)
  const loaders = useAppSelector((state) => state.defaultSlice.loaders)
  const globalError = useAppSelector((state) => state.defaultSlice.globalError)
  const errorMessage = useAppSelector(
    (state) => state.defaultSlice.errorMessage,
  )
  const showErrorTitle = useAppSelector(
    (state) => state.defaultSlice.showErrorTitle,
  )
  const sessionData = useAppSelector((state) => state.defaultSlice.sessionData)
  const globalHtmlDir = useAppSelector(
    (state) => state.defaultSlice.config?.options?.textDir,
  )

  useEffect(() => {
    if (globalHtmlDir) {
      document.documentElement.setAttribute('dir', globalHtmlDir)
    } else {
      document.documentElement.removeAttribute('dir')
    }
  }, [globalHtmlDir])
  const onErrorCallback = useAppSelector(
    (state) => state.defaultSlice.onErrorCallback,
  )
  const { errors = {} } = (config?.texts || {}) as ITextLibrary
  const {
    globalError: errorTitle = '',
    backToWhatsapp = `Send 'Hi' to Chatbot`,
    botMessage = 'Hi',
    tryAgain = 'Try Again',
  } = flatTexts(errors)
  const onErrorAction: ActionType = onErrorCallback
    ? ActionType.reload
    : ActionType.navigate
  const buttonErrorText = onErrorCallback ? tryAgain : backToWhatsapp
  const phoneNumber = sessionData?.workflow?.channelUid || config?.phoneNumber
  const channel = sessionData?.workflow?.channel || WorkflowChannel.WHATSAPP
  let errorCallBack = onErrorCallback
  if (phoneNumber) {
    const goToChannelUrl = getGoToChannelUrl({
      channel,
      phoneNumber,
      text: botMessage,
    })
    errorCallBack = () => (window.location.href = goToChannelUrl)
  }

  useMetaTags(
    {
      title: config?.metatags?.title,
      description: config?.metatags?.description,
      charset: config?.metatags?.charset,
      lang: config?.metatags?.lang,
      metas: [{ name: 'keywords', content: config?.metatags?.keywords }],
      links: [{ rel: 'icon', href: config?.metatags?.favicon }],
    },
    [],
  )

  /*
   * The route table is held back by the *boot* loaders, and by nothing else.
   *
   * It used to be gated on `loading`, which `addLoader` raises for any spinner
   * at all — including the one `useCreateOrderHook` raises around order
   * creation. Registering no routes deletes the matched one, so raising that
   * spinner unmounted the container the shopper was checking out from, and
   * with it the `usePlaceOrderFlow` effect that owns `pushAfter`. The confirm
   * dialog's "yes" emits `createOrder` and `placeOrderFlowNext` from one
   * synchronous `processActions` reduce, so the loader and `isDone: true` land
   * in the same render: React deletes that subtree instead of running its
   * effect, and the hook's unmount cleanup then dispatches
   * `clearPlaceOrderActions`, putting `isDone` back to `false` so the push
   * could not happen later either. The order was created and the shopper
   * stayed on the cart (COM-858), behind a one-frame flash of `NotFound` —
   * the catch-all is all that is left when the map registers nothing.
   *
   * The boot loaders keep the gate they have always had, and the reason is
   * *where they are raised from*, not what they are called. All three dispatch
   * sites — `redux/slices/config/config.ts` (`STORE_CONFIG`, inside
   * `fetchConfig`), `hooks/catalog/useInitialCatalogData.ts` and
   * `hooks/categories/useInitialCategoriesData.ts` (`INITIAL_DATA`) — are
   * reached only from `Config`'s effects, and `Config` wraps `<Routes>`.
   * `CREATE_ORDER` is raised from `hooks/useCreateOrder.ts`, under
   * `EventManager`, i.e. *below* the route table: it is the only loader whose
   * raiser the gate would delete. Adding a caller to any of those three sites
   * is what would break this, so check that before exempting anything else;
   * containers must not mount over a catalogue that has not arrived. Anything not named
   * here is treated as a boot loader, so a loader added later keeps today's
   * behaviour until it is deliberately exempted.
   *
   * `WORKFLOW_STEP` is the second deliberate exemption, for the same reason
   * as `CREATE_ORDER`: `hooks/workflow/useWorkflowStep.ts` raises it from
   * under `EventManager`, so gating on it would unmount the instance that is
   * waiting for the step and leave its outcome nowhere to run.
   *
   * `RELOAD_PAGE` is the third (COM-1521). `utils/reloadPage.ts` raises it
   * from the handler `EventManager` registers, also below the route table,
   * while it waits for cart writes before the page loads. Gated, it would
   * swap the screen for the catch-all for that wait, and the splash is
   * there to stop taps, not to unmount anything.
   */
  const isBooting = loaders.some((name) => !NON_BOOT_LOADERS.includes(name))

  const { baseName } = getUrlParams(window.location.pathname)
  return (
    <BrowserRouter basename={baseName}>
      <Config>
        {globalError ? (
          <ErrorMessage
            message={errorMessage || ''}
            title={showErrorTitle ? errorTitle.toString() : ''}
            callbackMessage={buttonErrorText.toString()}
            callBack={errorCallBack}
            action={onErrorAction}
            channel={channel}
          />
        ) : (
          <Routes>
            {!isBooting
              ? config &&
                config.routes &&
                config.routes.map(({ route, exact, container }) => (
                  <Route
                    path={route}
                    key={route}
                    element={
                      <ErrorBoundaries>
                        <BaseContainer
                          container={config.containers[container]}
                          containerName={container}
                          modals={config.modals || {}}
                        />
                        {children}
                      </ErrorBoundaries>
                    }
                  />
                ))
              : null}
            <Route path="*" element={<NotFound />} />
          </Routes>
        )}
      </Config>
    </BrowserRouter>
  )
}

export default Index
